import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { HOME } from "@/constants/testIds";

const links = [
  { href: "/#programs", label: "Programs" },
  { href: "/#skills", label: "Skills" },
  { href: "/#founders", label: "Founders" },
  { href: "/#colleges", label: "Colleges" },
  { href: "/#employers", label: "Employers" },
  { href: "/#faq", label: "FAQ" },
  { href: "/#contact", label: "Contact" },
];

export default function Navbar({ logoUrl }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname, loc.hash]);

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-[background,box-shadow] duration-300 ${scrolled ? "glass-light shadow-soft" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-16 sm:h-20">
        <Link to="/" data-testid={HOME.navLogo} className="flex items-center gap-3" aria-label="Be Hire Ready — home">
          <img src={logoUrl} alt="Be Hire Ready logo" className="h-10 w-10 sm:h-11 sm:w-11 rounded-lg object-cover ring-1 ring-slate-200" />
          <div className="leading-tight hidden sm:block">
            <div className={`font-display font-semibold text-[15px] transition-colors ${scrolled ? "text-[#0B132B]" : "text-white"}`}>Be Hire Ready</div>
            <div className={`text-[10px] tracking-[0.24em] uppercase transition-colors ${scrolled ? "text-slate-500" : "text-slate-200"}`}>Prepare · Improve · Succeed</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <a key={l.href} href={l.href} data-testid={`nav-link-${l.label.toLowerCase()}`}
               className={`px-3.5 py-2 text-[13.5px] rounded-full transition-colors ${
                 scrolled ? "text-slate-700 hover:text-[#0B132B] hover:bg-slate-100" : "text-slate-100 hover:text-white hover:bg-white/10"
               }`}>
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a href="#contact" data-testid="nav-book-btn"
             className={`hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium hover:-translate-y-0.5 transition-transform shadow-elev ${
               scrolled ? "bg-[#0B132B] text-white" : "bg-white text-[#0B132B]"
             }`}>
            Book Free Counselling
          </a>
          <button onClick={() => setOpen((v) => !v)} data-testid={HOME.navMobileToggle}
                  className={`lg:hidden p-2 rounded-full ring-1 ${scrolled ? "ring-slate-200 bg-white text-[#0B132B]" : "ring-white/20 bg-white/10 text-white"}`}>
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      className="lg:hidden overflow-hidden bg-white border-t border-slate-100">
            <div className="px-5 py-4 flex flex-col gap-1">
              {links.map((l) => (
                <a key={l.href} href={l.href} className="py-2 text-sm text-slate-700">{l.label}</a>
              ))}
              <a href="#contact" className="mt-2 inline-flex justify-center px-5 py-3 rounded-full bg-[#0B132B] text-white text-sm">Book Free Counselling</a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
